// /pages/HR/components/filters/FilterBar.js
import React, { useState, useCallback } from 'react';
import DateRangeFilter from './DateRangeFilter';
import LocationFilter from './LocationFilter';
import CategoryFilter from './CategoryFilter';
import './FilterBar.css';

// Status options - matching database values
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'new', label: 'New', color: '#8B5CF6' },
  { value: 'in_progress', label: 'In Progress', color: '#3B82F6' },
  { value: 'pending', label: 'Pending', color: '#F59E0B' },
  { value: 'resolved', label: 'Resolved', color: '#10B981' },
  { value: 'cancelled', label: 'Cancelled', color: '#6B7280' },
];

const FilterBar = React.memo(({
  onFilterChange = () => {},
  showDateRange = true,
  showLocation = true,
  showCategory = true,
  showStatus = true,
  showSearch = true,
  defaultFilters = {},
  className = '',
  inline = false,
  compact = false,
}) => {
  const [filters, setFilters] = useState(() => ({
    createdDateRange: { startDate: null, endDate: null },
    resolvedDateRange: { startDate: null, endDate: null },
    locations: [],
    categories: [],
    status: 'all',
    search: '',
    ...defaultFilters
  }));

  // Update filter state
  const updateFilter = useCallback((filterType, value) => {
    setFilters(prevFilters => {
      const newFilters = {
        ...prevFilters,
        [filterType]: value
      };

      onFilterChange(newFilters);
      return newFilters;
    });
  }, [onFilterChange]);

  // Handle search change
  const handleSearchChange = useCallback((e) => {
    updateFilter('search', e.target.value);
  }, [updateFilter]);

  // Handle status change
  const handleStatusChange = useCallback((e) => {
    updateFilter('status', e.target.value);
  }, [updateFilter]);

  // Handle created date range change
  const handleCreatedDateChange = useCallback((dateRange) => {
    updateFilter('createdDateRange', dateRange);
  }, [updateFilter]);

  // Handle resolved date range change
  const handleResolvedDateChange = useCallback((dateRange) => {
    updateFilter('resolvedDateRange', dateRange);
  }, [updateFilter]);

  const filterClasses = `filter-bar ${inline ? 'filter-bar-inline' : ''} ${compact ? 'filter-bar-compact' : ''} ${className}`;

  return (
    <div className={filterClasses}>
      <div className="filter-controls">
        {/* Search Input */}
        {showSearch && (
          <div className="filter-search">
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <span className="search-icon">⌕</span>
          </div>
        )}

        {/* Created Date Range Filter */}
        {showDateRange && (
          <DateRangeFilter
            startDate={filters.createdDateRange?.startDate}
            endDate={filters.createdDateRange?.endDate}
            onDateChange={handleCreatedDateChange}
            label="Created"
            placeholder="All dates"
            className="filter-item"
          />
        )}

        {/* Resolved Date Range Filter */}
        {showDateRange && (
          <DateRangeFilter
            startDate={filters.resolvedDateRange?.startDate}
            endDate={filters.resolvedDateRange?.endDate}
            onDateChange={handleResolvedDateChange}
            label="Resolved"
            placeholder="All dates"
            className="filter-item"
          />
        )}

        {/* Location Filter */}
        {showLocation && (
          <LocationFilter
            selectedLocations={filters.locations}
            onLocationChange={(locations) => updateFilter('locations', locations)}
            className="filter-item"
          />
        )}

        {/* Category Filter */}
        {showCategory && (
          <CategoryFilter
            selectedCategories={filters.categories}
            onCategoryChange={(categories) => updateFilter('categories', categories)}
            className="filter-item"
          />
        )}

        {/* Status Filter */}
        {showStatus && (
          <div className="status-filter filter-item">
            <select
              value={filters.status}
              onChange={handleStatusChange}
              className="status-select"
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
});

FilterBar.displayName = 'FilterBar';

export default FilterBar;
